import { useMemo, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowUpRight,
  Briefcase,
  Building2,
  Car,
  ChevronDown,
  Home,
  Landmark,
  MoreHorizontal,
  Plane,
  Ship,
  Smartphone,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { StarlinkComtradeMap } from '@/components/value-chain/explorer/StarlinkComtradeMap';
import { CoverageStatusBar, StarlinkDemandMap, useStarlinkMarkets } from '@/components/value-chain/explorer/StarlinkDemandMap';
import { StarlinkGlobalOverview } from '@/components/value-chain/explorer/StarlinkGlobalOverview';
import { StarlinkIndependentSeries } from '@/components/value-chain/explorer/StarlinkIndependentSeries';
import { InvestorIdentityBanner, StarlinkInvestorDesk } from '@/components/value-chain/explorer/StarlinkInvestorDesk';
import { StarlinkTradeEvidence } from '@/components/value-chain/explorer/StarlinkTradeEvidence';
import { INVESTOR_DOCS, INVESTOR_GROUPS, kindLabel, rowsFor } from '@/components/value-chain/starlinkInvestor';
import {
  STARLINK_DESCRIPTION,
  STARLINK_HISTORY,
  STARLINK_PERIODS,
  STARLINK_PRODUCTS,
  STARLINK_TABS,
  STARLINK_TAGLINE,
  STARLINK_TAKEAWAYS,
  STARLINK_UPDATES,
  getStarlinkSnapshot,
  type PeriodId,
  type StarlinkLeaf,
  type StarlinkProductCard,
  type StarlinkSegment,
  type StarlinkSnapshot,
  type StarlinkTabId,
} from '@/components/value-chain/starlinkProduct';

type Props = {
  onBack: () => void;
  onOpenValueChain: () => void;
  onOpenSuppliers: () => void;
  onOpenConsumer?: () => void;
  initialTab?: StarlinkTabId;
};

const LEAF_ICON: Record<StarlinkLeaf['glyph'], LucideIcon> = {
  home: Home,
  car: Car,
  briefcase: Briefcase,
  buildings: Building2,
  ship: Ship,
  plane: Plane,
  truck: Truck,
  landmark: Landmark,
  dish: Home,
  mini: Home,
  performance: Home,
  ellipsis: MoreHorizontal,
};

export function StarlinkProductView({
  onBack,
  onOpenValueChain,
  onOpenSuppliers,
  onOpenConsumer: _onOpenConsumer,
  initialTab = 'overview',
}: Props) {
  const [tab, setTab] = useState<StarlinkTabId>(initialTab);
  const [periodId, setPeriodId] = useState<PeriodId>('q2-2026');
  const [periodOpen, setPeriodOpen] = useState(false);
  const snapshot = getStarlinkSnapshot(periodId);

  const handleTab = (id: StarlinkTabId) => {
    if (id === 'value-chain') {
      onOpenValueChain();
      return;
    }
    if (id === 'suppliers') {
      onOpenSuppliers();
      return;
    }
    setTab(id);
  };

  if (tab === 'overview') {
    return (
      <StarlinkGlobalOverview
        onBack={onBack}
        onOpenFinancials={() => setTab('revenue')}
        onOpenValueChain={onOpenValueChain}
        onOpenIndependent={() => setTab('independent')}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f7f8fa]">
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-1 text-[12px] text-slate-400">
            <span>Companies</span>
            <span>›</span>
            <button type="button" onClick={onBack} className="hover:text-slate-700">
              SpaceX
            </button>
            <span>›</span>
            <span className="font-medium text-slate-700">Starlink</span>
          </nav>
          <div className="relative">
            <button
              type="button"
              onClick={() => setPeriodOpen((open) => !open)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700"
            >
              {snapshot.periodLabel}
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            {periodOpen ? (
              <div className="absolute right-0 z-20 mt-1 w-44 rounded-xl border border-slate-200 bg-white py-1 shadow-sm">
                {STARLINK_PERIODS.map((period) => (
                  <button
                    key={period.id}
                    type="button"
                    onClick={() => {
                      setPeriodId(period.id);
                      setPeriodOpen(false);
                    }}
                    className={`block w-full px-3 py-2 text-left text-[12px] ${
                      period.id === periodId ? 'font-medium text-slate-900' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <header className="mb-4 flex flex-wrap items-start justify-between gap-6">
          <div className="flex min-w-0 items-start gap-3">
            <StarlinkMark />
            <div className="min-w-0">
              <h1 className="text-[28px] leading-tight font-semibold tracking-tight text-slate-900">
                Starlink
              </h1>
              <p className="mt-0.5 text-[13px] font-medium text-slate-500">{STARLINK_TAGLINE}</p>
              <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-slate-400">
                {STARLINK_DESCRIPTION}
              </p>
            </div>
          </div>
          <dl className="flex flex-wrap items-end gap-x-7 gap-y-3">
            {snapshot.kpis.map((kpi) => (
              <div key={kpi.label} className="text-right">
                <dd className="text-[22px] leading-none font-semibold tracking-tight text-slate-900">
                  {kpi.value}
                </dd>
                <dt className="mt-1 text-[11px] text-slate-400">{kpi.label}</dt>
                {kpi.delta ? (
                  <p
                    className={`mt-0.5 text-[11px] font-semibold ${
                      kpi.down ? 'text-rose-500' : 'text-emerald-600'
                    }`}
                  >
                    {kpi.down ? '↓' : '↑'} {kpi.delta}
                  </p>
                ) : null}
              </div>
            ))}
          </dl>
        </header>

        <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
          {STARLINK_TABS.map((item) => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleTab(item.id)}
                className={`relative px-3 py-2.5 text-[13px] ${
                  selected ? 'font-medium text-blue-700' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {item.label}
                {selected ? (
                  <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-blue-600" />
                ) : null}
              </button>
            );
          })}
        </div>

        {tab === 'independent' ? <StarlinkIndependentSeries /> : null}
        {tab === 'revenue' ? <RevenueTab snapshot={snapshot} /> : null}
        {tab === 'investor' ? <StarlinkInvestorDesk /> : null}
        {tab === 'products' ? <ProductsTab /> : null}
        {tab === 'infrastructure' ? <InfrastructureTab /> : null}
        {tab === 'key-data' ? <KeyDataTab /> : null}
        {tab === 'documents' ? <DocumentsTab /> : null}
      </div>
    </div>
  );
}

export function OverviewTab({
  snapshot,
  onViewProducts,
  onViewDocuments,
  onOpenInvestor,
  onOpenIndependent,
  onOpenConsumer,
}: {
  snapshot: StarlinkSnapshot;
  onViewProducts: () => void;
  onViewDocuments: () => void;
  onOpenInvestor: () => void;
  onOpenIndependent: () => void;
  onOpenConsumer?: () => void;
}) {
  return (
    <div className="space-y-3 pb-4">
      <InvestorIdentityBanner />
      <div className="flex justify-end gap-4">
        <button
          type="button"
          onClick={onOpenIndependent}
          className="text-[12px] font-medium text-blue-700 hover:text-blue-900"
        >
          Independent series →
        </button>
        <button
          type="button"
          onClick={onOpenInvestor}
          className="text-[12px] font-medium text-blue-700 hover:text-blue-900"
        >
          Open investor desk →
        </button>
      </div>
      <StarlinkIndependentSeries />
      <StarlinkDemandMap />
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Key Takeaways</h3>
          {onOpenConsumer ? (
            <button
              type="button"
              onClick={onOpenConsumer}
              className="text-[11px] font-medium text-blue-700 hover:text-blue-900"
            >
              Consumer ecosystem →
            </button>
          ) : null}
        </div>
          <ol className="mt-3 space-y-2.5">
            {STARLINK_TAKEAWAYS.map((insight, i) => (
              <li key={insight} className="flex gap-2.5 text-[12px] leading-relaxed text-slate-600">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-medium text-white">
                  {i + 1}
                </span>
                <span>{insight}</span>
              </li>
            ))}
          </ol>
        </section>

      <StarlinkTradeEvidence />

      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
        <ChartCard
          title="Subscriber Growth"
          value="12.0M"
          sub="EoP Jun 30, 2026"
          delta="+101.2% YoY"
        >
          <SubscriberChart />
        </ChartCard>
        <ChartCard
          title="Revenue Trend (USD B)"
          value={snapshot.connectivityDisplay.replace('$', '')}
          sub={`Connectivity (${snapshot.headingPeriod})`}
          delta={snapshot.connectivityYoY}
          legend={
            <ul className="flex flex-wrap gap-2 text-[10px] text-slate-500">
              <LegendDot color="#3b82f6" label="Consumer" />
              <LegendDot color="#34d399" label="Enterprise & Gov" />
              <LegendDot color="#a78bfa" label="Kits" />
            </ul>
          }
        >
          <RevenueChart />
        </ChartCard>
        <CoverageChartCard />
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-slate-800">Latest Updates</h3>
            <button
              type="button"
              onClick={onViewDocuments}
              className="text-[11px] font-medium text-blue-700 hover:text-blue-900"
            >
              View all →
            </button>
          </div>
          <ul className="space-y-3">
            {STARLINK_UPDATES.map((item) => (
              <li key={item.title} className="flex gap-3">
                <span className="w-[88px] shrink-0 text-[11px] text-slate-400">{item.date}</span>
                <span>
                  <span className="block text-[12px] font-medium text-slate-800">{item.title}</span>
                  <span className="block text-[11px] text-slate-500">{item.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-slate-900">Starlink Product Lineup</h3>
          <button
            type="button"
            onClick={onViewProducts}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-700 hover:text-blue-900"
          >
            View all products
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {STARLINK_PRODUCTS.map((product) => (
            <ProductCard key={product.id} product={product} compact />
          ))}
        </div>
      </section>
    </div>
  );
}

function RevenueTab({
  snapshot,
}: {
  snapshot: StarlinkSnapshot;
}) {
  return (
    <div className="space-y-3 pb-4">
      <InvestorIdentityBanner />
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-[13px] font-semibold text-slate-800">Backlog is not Consumer</h3>
        <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
          Note 3 backlog of $47,461 million (56% within one year) and deferred revenue of $14,286 million
          are Space plus Connectivity enterprise and government. Consumer is billed as delivered and is
          excluded from backlog. Starlink Mobile is parked in Enterprise &amp; Government.
        </p>
        <dl className="mt-3 grid gap-2 sm:grid-cols-3 text-[12px]">
          <div className="rounded-xl bg-slate-50 px-3 py-2">
            <dt className="text-slate-500">Backlog Jun 30</dt>
            <dd className="mt-0.5 text-[18px] font-semibold text-slate-900">$47.5B</dd>
          </div>
          <div className="rounded-xl bg-slate-50 px-3 py-2">
            <dt className="text-slate-500">Deferred revenue</dt>
            <dd className="mt-0.5 text-[18px] font-semibold text-slate-900">$14.3B</dd>
          </div>
          <div className="rounded-xl bg-slate-50 px-3 py-2">
            <dt className="text-slate-500">FY25 Connectivity op. income</dt>
            <dd className="mt-0.5 text-[18px] font-semibold text-slate-900">$4.42B</dd>
          </div>
        </dl>
      </section>
      <StarlinkDemandMap />
      <StarlinkComtradeMap />
      <StarlinkTradeEvidence />
      <div className="grid gap-3 lg:grid-cols-2">
        <ChartCard
          title="Revenue Trend (USD B)"
          value={snapshot.connectivityDisplay.replace('$', '')}
          sub="Connectivity"
          delta={snapshot.connectivityYoY}
        >
          <RevenueChart />
        </ChartCard>
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-[13px] font-semibold text-slate-800">
            Printed lines ({snapshot.headingPeriod})
          </h3>
          <table className="w-full text-left text-[12px]">
            <tbody>
              {snapshot.segments.map((segment) => (
                <tr key={segment.id} className="border-b border-slate-100">
                  <td className="py-2">
                    <span className="mr-2 inline-block h-2 w-2 rounded-sm" style={{ background: segment.color }} />
                    {segment.label}
                  </td>
                  <td className="py-2 text-right font-semibold tabular-nums">{segment.display}</td>
                  <td className="py-2 text-right text-slate-500">{segment.shareLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function ProductsTab() {
  return (
    <div className="grid gap-3 pb-4 sm:grid-cols-2 xl:grid-cols-3">
      {STARLINK_PRODUCTS.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}

function InfrastructureTab() {
  const payload = useStarlinkMarkets();
  return (
    <div className="space-y-3 pb-4">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-[15px] font-semibold text-slate-900">Global Coverage</h2>
          <p className="mb-3 text-[12px] text-slate-500">
            Official Starlink availability.json, not the stylized coverage graphic.
          </p>
          <CoverageMap />
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h2 className="text-[15px] font-semibold text-slate-900">Network (printed vs live)</h2>
          <dl className="mt-4 space-y-3 text-[13px]">
            <InfraRow label="Operational satellites (Jun 30)" value="10,200+" />
            <InfraRow label="Satellites (Mar 31, 2026)" value="~9,600" />
            <InfraRow label="V1 Mobile satellites (Mar 31)" value="~650" />
            <InfraRow label="Live markets (availability.json)" value={String(payload.kpis.live)} />
            <InfraRow label="Markets (10-Q MD&A)" value="167" />
            <InfraRow label="Direct-to-cell countries (Mar 31)" value="~30" />
            <InfraRow label="Subscribers (Jun 30)" value="12.0 million" />
            <InfraRow label="Mobile unique devices (Mar 31)" value="~7.4 million" />
            <InfraRow label="V3 downlink (design)" value="1 Tbps / satellite" />
            <p className="text-[12px] leading-relaxed text-slate-500">
              MD&amp;A footprint is installed capacity, not a geographic split of Consumer dollars. Mobile
              unique devices are not the 12.0 million broadband KPI. Independent CelesTrak / McDowell
              counts check the 10,200 print; they do not replace it.
            </p>
          </dl>
        </section>
      </div>
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="text-[15px] font-semibold text-slate-900">Vertical attach (company report, not Note 3 mix)</h2>
        <p className="mt-1 text-[12px] text-slate-500">
          Starlink 2025 progress report operational counts. They do not split the $1,806 million E&amp;G
          print.
        </p>
        <dl className="mt-3 grid gap-2 sm:grid-cols-3">
          <div className="rounded-xl bg-slate-50 px-3 py-2.5">
            <dt className="text-[11px] text-slate-500">Vessels connected</dt>
            <dd className="text-[20px] font-semibold text-slate-900">&gt;150,000</dd>
          </div>
          <div className="rounded-xl bg-slate-50 px-3 py-2.5">
            <dt className="text-[11px] text-slate-500">Commercial aircraft equipped in 2025</dt>
            <dd className="text-[20px] font-semibold text-slate-900">&gt;1,400</dd>
          </div>
          <div className="rounded-xl bg-slate-50 px-3 py-2.5">
            <dt className="text-[11px] text-slate-500">Business jets equipped</dt>
            <dd className="text-[20px] font-semibold text-slate-900">&gt;1,000</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

function InfraRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 pb-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function KeyDataTab() {
  return (
    <div className="space-y-3 pb-6">
      {INVESTOR_GROUPS.filter((group) => group.id !== 'proxies').map((group) => (
        <section key={group.id} className="rounded-2xl border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-[15px] font-semibold text-slate-900">{group.label}</h2>
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] tracking-wide text-slate-400 uppercase">
                <th className="pb-2 pr-3 font-medium">Metric</th>
                <th className="pb-2 pr-3 font-medium">Value</th>
                <th className="pb-2 pr-3 font-medium">Kind</th>
                <th className="pb-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {rowsFor(group.id).map((row) => (
                <tr key={row.metric} className="border-b border-slate-100 align-top">
                  <td className="py-2.5 pr-3 text-slate-700">{row.metric}</td>
                  <td className="py-2.5 pr-3 font-semibold text-slate-900">{row.value}</td>
                  <td className="py-2.5 pr-3 text-[11px] text-slate-400">{kindLabel(row.kind)}</td>
                  <td className="py-2.5 text-slate-500">{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      <p className="text-[11px] text-slate-400">
        Q1 2026 Consumer / E&amp;G / kits are H1 minus Q2 from Note 3. March 31 subscribers are from EX-99.1.
        FY 2025 Connectivity is from the 424(b)(4). Sub-segment dollar mix is unprinted. Independent proxies
        live on the Investor desk and the markets / trade maps.
      </p>
    </div>
  );
}

function DocumentsTab() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-[15px] font-semibold text-slate-900">Documents</h2>
      <ul className="divide-y divide-slate-100">
        {INVESTOR_DOCS.map((item) => (
          <li key={item.title} className="flex items-start justify-between gap-4 py-3">
            <div>
              {item.href ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] font-medium text-blue-700 hover:text-blue-900"
                >
                  {item.title}
                </a>
              ) : (
                <p className="text-[13px] font-medium text-slate-900">{item.title}</p>
              )}
              <p className="text-[12px] text-slate-500">{item.detail}</p>
            </div>
            <span className="shrink-0 text-[12px] text-slate-400">{item.date}</span>
          </li>
        ))}
        {STARLINK_UPDATES.map((item) => (
          <li key={item.title} className="flex items-start justify-between gap-4 py-3">
            <div>
              <p className="text-[13px] font-medium text-slate-900">{item.title}</p>
              <p className="text-[12px] text-slate-500">{item.detail}</p>
            </div>
            <span className="shrink-0 text-[12px] text-slate-400">{item.date}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function SegmentTreemap({
  segments,
  onOpenConsumer,
}: {
  segments: StarlinkSegment[];
  onOpenConsumer?: () => void;
}) {
  return (
    <div className="grid min-h-[280px] gap-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.05fr)_minmax(200px,0.9fr)]">
      {segments.map((segment) => (
        <SegmentColumn
          key={segment.id}
          segment={segment}
          onOpen={segment.id === 'consumer' ? onOpenConsumer : undefined}
        />
      ))}
    </div>
  );
}

function SegmentColumn({
  segment,
  onOpen,
}: {
  segment: StarlinkSegment;
  onOpen?: () => void;
}) {
  return (
    <article
      className={`flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl p-3 text-white ${
        onOpen ? 'cursor-pointer transition hover:brightness-105' : ''
      }`}
      style={{ background: segment.color }}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
    >
      <header className="mb-2 shrink-0">
        <p className="text-[13px] font-medium">{segment.label}</p>
        <p className="text-[28px] leading-none font-semibold tracking-tight">{segment.display}</p>
        <p className="mt-1 text-[11px] text-white/85">
          {segment.shareLabel}
          {segment.id === 'hardware' ? <sup>1</sup> : null}
        </p>
      </header>
      <div className="min-h-0 flex-1">
        {segment.id === 'consumer' ? <ConsumerLeaves leaves={segment.leaves} tile={segment.tile} /> : null}
        {segment.id === 'enterprise' ? <EnterpriseLeaves leaves={segment.leaves} tile={segment.tile} /> : null}
        {segment.id === 'hardware' ? <HardwareLeaves leaves={segment.leaves} tile={segment.tile} /> : null}
      </div>
    </article>
  );
}

function ConsumerLeaves({ leaves, tile }: { leaves: StarlinkLeaf[]; tile: string }) {
  const [personal, selfServe] = leaves;
  return (
    <div className="grid h-full min-h-[160px] grid-rows-2 gap-1.5">
      <LeafTile leaf={personal} tile={tile} />
      <LeafTile leaf={selfServe} tile={tile} />
    </div>
  );
}

function EnterpriseLeaves({ leaves, tile }: { leaves: StarlinkLeaf[]; tile: string }) {
  const [government, aviation, maritime, other, mobile] = leaves;
  return (
    <div className="grid h-full min-h-[160px] grid-cols-2 grid-rows-3 gap-1.5">
      <LeafTile leaf={government} tile={tile} />
      <LeafTile leaf={aviation} tile={tile} />
      <LeafTile leaf={maritime} tile={tile} />
      <LeafTile leaf={other} tile={tile} />
      <LeafTile leaf={mobile} tile={tile} className="col-span-2" />
    </div>
  );
}

function HardwareLeaves({ leaves, tile }: { leaves: StarlinkLeaf[]; tile: string }) {
  return (
    <div className="grid h-full min-h-[160px] gap-1.5">
      <LeafTile leaf={leaves[0]} tile={tile} showDish />
    </div>
  );
}

function LeafTile({
  leaf,
  tile,
  className = '',
  showDish = false,
}: {
  leaf: StarlinkLeaf | undefined;
  tile: string;
  className?: string;
  showDish?: boolean;
}) {
  if (!leaf) return null;
  const Icon = LEAF_ICON[leaf.glyph];
  const dish = showDish || leaf.glyph === 'dish' || leaf.glyph === 'mini' || leaf.glyph === 'performance';
  return (
    <div className={`relative min-h-0 overflow-hidden rounded-xl p-2.5 ${className}`} style={{ background: tile }}>
      <div className="relative z-10 flex h-full min-h-0 flex-col justify-between">
        <span className="flex items-start justify-between gap-1">
          <span className="text-[11px] leading-tight font-medium">{leaf.label}</span>
          {dish ? null : <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-80" />}
        </span>
        <span className="text-[10px] text-white/80">{leaf.detail}</span>
      </div>
      {dish ? (
        <span className="pointer-events-none absolute right-1 bottom-0 opacity-90">
          <DishGlyph kind={leaf.glyph === 'mini' ? 'mini' : 'standard'} />
        </span>
      ) : null}
    </div>
  );
}

export function SegmentBars({ segments }: { segments: StarlinkSegment[] }) {
  const data = segments.map((segment) => ({
    name: segment.id === 'enterprise' ? 'Enterprise & Gov' : segment.id === 'hardware' ? 'Kits' : segment.label,
    revenueB: segment.revenue / 1_000_000_000,
    color: segment.color,
    display: segment.display,
  }));
  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barSize={42} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
          <YAxis
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => `$${value}B`}
          />
          <Tooltip
            formatter={(value) => [`$${Number(value).toFixed(2)}B`, 'Revenue']}
            contentStyle={tooltipStyle}
          />
          <Bar dataKey="revenueB" radius={[8, 8, 0, 0]}>
            {data.map((row) => (
              <Cell key={row.name} fill={row.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SubscriberChart() {
  const data = STARLINK_HISTORY.filter((row) => row.subscribersM != null);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} barSize={28} margin={{ top: 18, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#eef2f7" />
        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          axisLine={false}
          tickLine={false}
          ticks={[0, 5, 10, 15]}
          domain={[0, 15]}
        />
        <Tooltip
          formatter={(value) => [`${Number(value).toFixed(1)}M`, 'Subscribers']}
          contentStyle={tooltipStyle}
        />
        <Bar dataKey="subscribersM" fill="#60a5fa" radius={[3, 3, 0, 0]}>
          {data.map((row) => (
            <Cell key={row.label} fill={row.label === "Q2'26" ? '#2563eb' : '#93c5fd'} />
          ))}
          <LabelList
            dataKey="subscribersM"
            position="top"
            content={(props) => {
              const { x, y, width, value } = props;
              if (value == null || x == null || y == null || width == null) return null;
              return (
                <text
                  x={Number(x) + Number(width) / 2}
                  y={Number(y) - 6}
                  textAnchor="middle"
                  fill="#2563eb"
                  fontSize={10}
                  fontWeight={600}
                >
                  {Number(value).toFixed(1)}M
                </text>
              );
            }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function RevenueChart() {
  const data = useMemo(
    () =>
      STARLINK_HISTORY.map((row) => ({
        ...row,
        consumer: row.consumerM / 1000,
        enterprise: row.enterpriseM / 1000,
        hardware: row.hardwareM / 1000,
      })),
    [],
  );
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} barSize={22} margin={{ top: 18, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#eef2f7" />
        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
        <Tooltip
          formatter={(value, name) => [`$${Number(value).toFixed(2)}B`, String(name)]}
          contentStyle={tooltipStyle}
        />
        <Bar dataKey="consumer" name="Consumer" stackId="rev" fill="#3b82f6" />
        <Bar dataKey="enterprise" name="Enterprise & Gov" stackId="rev" fill="#34d399" />
        <Bar dataKey="hardware" name="Kits" stackId="rev" fill="#a78bfa" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CoverageChartCard() {
  const payload = useStarlinkMarkets();
  return (
    <ChartCard
      title="Global Coverage"
      value={String(payload.kpis.live)}
      sub="Live in availability.json"
    >
      <CoverageMap compact />
    </ChartCard>
  );
}

export function CoverageMap({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? 'h-full' : undefined}>
      <CoverageStatusBar compact={compact} />
    </div>
  );
}

function ProductCard({ product, compact = false }: { product: StarlinkProductCard; compact?: boolean }) {
  return (
    <article
      className={`flex flex-col rounded-2xl border border-slate-200 bg-white ${compact ? 'p-3' : 'p-4'}`}
    >
      <div className="flex h-16 items-center justify-center">
        <ProductGlyph kind={product.kind} />
      </div>
      <h4 className="mt-1 text-[13px] font-semibold text-slate-900">{product.name}</h4>
      <p className="text-[11px] text-slate-500">{product.subtitle}</p>
      {compact ? null : <p className="mt-2 text-[12px] leading-relaxed text-slate-500">{product.detail}</p>}
    </article>
  );
}

function ChartCard({
  title,
  value,
  sub,
  delta,
  legend,
  children,
}: {
  title: string;
  value: string;
  sub: string;
  delta?: string;
  legend?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex h-[250px] flex-col rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-[13px] font-semibold text-slate-800">{title}</h3>
          <div className="mt-1 flex items-end gap-2">
            <p className="text-[22px] leading-none font-semibold tracking-tight text-slate-900">{value}</p>
            {delta ? <span className="text-[11px] font-semibold text-emerald-600">{delta}</span> : null}
          </div>
          <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>
        </div>
        {legend}
      </div>
      <div className="mt-1 min-h-0 flex-1">{children}</div>
    </section>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <li className="inline-flex items-center gap-1">
      <span className="h-2 w-2 rounded-sm" style={{ background: color }} />
      {label}
    </li>
  );
}

function StarlinkMark() {
  return (
    <span className="mt-0.5 flex h-12 w-12 items-center justify-center text-slate-900" aria-hidden>
      <svg viewBox="0 0 64 64" className="h-11 w-11">
        <path d="M6 8h18L58 56H40L6 8Z" fill="currentColor" />
        <path d="M40 8h18L32 36h-12L40 8Z" fill="currentColor" />
      </svg>
    </span>
  );
}

function DishGlyph({ kind }: { kind: 'standard' | 'mini' }) {
  const scale = kind === 'mini' ? 0.82 : 1;
  return (
    <svg viewBox="0 0 88 56" width={kind === 'mini' ? 52 : 64} height={kind === 'mini' ? 34 : 42}>
      <g transform={`scale(${scale}) translate(${kind === 'mini' ? 8 : 0}, ${kind === 'mini' ? 6 : 0})`}>
        <ellipse cx="44" cy="42" rx="28" ry="5" fill="#0f172a" opacity="0.12" />
        <path d="M16 36c10-22 50-22 60 2-16 6-46 6-60-2Z" fill="#f8fafc" />
        <path d="M24 24c12-6 36-6 48 4" fill="none" stroke="#e2e8f0" strokeWidth="2" />
        <rect x="41" y="36" width="6" height="12" rx="1" fill="#e2e8f0" />
        <path d="M34 48h20l-3 5H37Z" fill="#cbd5e1" />
      </g>
    </svg>
  );
}

function ProductGlyph({ kind }: { kind: StarlinkProductCard['kind'] }) {
  if (kind === 'direct') {
    return <Smartphone className="h-8 w-8 text-slate-400" />;
  }
  if (kind === 'maritime') {
    return (
      <svg viewBox="0 0 72 48" className="h-12 w-16">
        <ellipse cx="36" cy="18" rx="16" ry="10" fill="#e2e8f0" />
        <rect x="33" y="18" width="6" height="10" fill="#cbd5e1" />
        <path d="M10 36h52l-6 8H16Z" fill="#94a3b8" />
        <path d="M18 36c4-8 32-8 36 0" fill="#64748b" />
      </svg>
    );
  }
  if (kind === 'aviation') {
    return <Plane className="h-8 w-8 text-slate-400" />;
  }
  return <DishGlyph kind={kind === 'mini' ? 'mini' : 'standard'} />;
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 10,
  border: '1px solid #e2e8f0',
};
